import { FormKitNode } from "@formkit/core";
import { getDefaultGridPreset, UserGridPreset } from "@q-food/q-food";
import type { FilterModel } from "ag-grid-community";
import { replaceVariables } from "~utils/Forms/Autocomplete.ts";

async function openGridLookup(node: FormKitNode, filters?: FilterModel) {
	const optionsSource = node.props.optionsSource as string;

	const rootNode = node.at("$root");
	const rootForm = rootNode?.type === "group" ? rootNode : undefined;
	const resource = replaceVariables(optionsSource, { ...node.props, node }, rootForm);

	const gridKey = node.props.attrs?.["grid-key"] as string | undefined;

	// No backend form field sets `grid-key`, so this used to call `getDefaultGridPreset(undefined)` on
	// every open — a guaranteed 404, swallowed by the catch.
	const gridPreset: UserGridPreset | undefined = gridKey
		? await getDefaultGridPreset(gridKey).catch((e) => {
				console.log(e);
				return undefined;
			})
		: undefined;

	node.emit(
		"autocompleteLookup",
		{
			gridKey,
			originalOptionsSource: node.props.optionsSource,
			originalOptionSource: node.props.optionSource,
			optionsSourceWithDependencies: resource,
			selectionMode: node.props?.multiple ? "multiRow" : "singleRow",
			filters: applyFilters(gridPreset, filters),
		},
		true,
	);
}

/**
 * Adds an event listener to the suffix icon of an autocomplete input.
 * The event listener will emit an event telling the autocomplete to open the grid lookup.
 */
const plugin = (node: FormKitNode) => {
	if (node.type === "input" && node.props.type === "autocomplete" && node.props.suffixIcon === "search") {
		node.props.onSuffixIconClick = () => {
			if (!node.props.disabled && !node.props.readonly) {
				openGridLookup(node);
			}
		};
	}
};

/**
 * The filter model the lookup grid should open on, or undefined when there is nothing to apply.
 *
 * Returning `"{}"` for "nothing" is not the same thing: `AppDataGrid` treats a defined `filters` prop
 * as an instruction and calls `setFilterModel` with it, so an empty object cost a second filter cycle
 * and a second server round trip on every open — long enough for the row count to land on one number
 * and then visibly change.
 */
function applyFilters(gridPreset?: UserGridPreset, filters?: FilterModel): string | undefined {
	let filterObject = {};
	if (gridPreset != null) {
		filterObject = JSON.parse(gridPreset.filterState);
	}
	if (filters != null) {
		filterObject = { ...filterObject, ...filters };
	}

	return Object.keys(filterObject).length > 0 ? JSON.stringify(filterObject) : undefined;
}

export { openGridLookup };
export default plugin;
